import React from 'react';
import { motion } from 'framer-motion';
import RatingStars from '../LeadsTable/RatingStars/RatingStars';
import StatusBadge from '../../../Dashboard/components/ResultsSection/ResultsTable/ResultsTableRow/StatusBadge/StatusBadge';
import ProspectedBadge from '../../../Dashboard/components/ResultsSection/ResultsTable/ResultsTableRow/ProspectedBadge/ProspectedBadge';
import { SavedLead } from '../../../../services/leadsService';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import { getWhatsAppLink } from '../../../../utils/whatsappUtils';

interface LeadDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: SavedLead | null;
}

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({
  isOpen,
  onClose,
  lead,
}) => {
  const { t } = useTranslation();

  if (!isOpen || !lead) return null;

  const dataItems = [
    { label: t('dashboard.detail_company'), value: lead.company_name },
    { label: t('dashboard.detail_website'), value: lead.website, isLink: true },
    { label: t('dashboard.detail_phone'), value: lead.phone_number },
    { label: t('leads.detail_email'), value: lead.email },
    { label: t('leads.detail_category'), value: lead.category },
    { label: t('leads.detail_address'), value: lead.address, fullWidth: true },
  ];

  const savedOnDate = new Date(lead.created_at).toLocaleDateString(i18n.language);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700/50 p-6 flex flex-col gap-6 max-h-[90vh] overflow-y-auto"
        initial={{ y: 50, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 50, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 dark:border-gray-700/50 pb-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-bold text-[#181411] dark:text-white break-words">{lead.company_name}</h2>
            <div className="flex items-center gap-3 mt-1">
              <RatingStars rating={lead.rating} />
              <StatusBadge status={lead.whatsapp_status} whatsapp={lead.whatsapp_status === 'Validado'} />
              <ProspectedBadge prospected={lead.prospected} />
            </div>
          </div>
          <motion.button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </motion.button>
        </div>

        {/* Body - Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {dataItems.map((item, index) => (
            <div key={index} className={item.fullWidth ? 'col-span-2' : 'col-span-1'}>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{item.label}</p>
              {item.isLink && item.value ? (
                <a
                  href={`http://${item.value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline break-words"
                >
                  {item.value}
                </a>
              ) : (
                <p className="text-sm text-[#181411] dark:text-white break-words">{item.value || 'N/A'}</p>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400">{t('leads.detail_saved_on')} {savedOnDate}</p>

        {/* Footer */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700/50">
          {lead.phone_number && getWhatsAppLink(lead.phone_number) && (
            <motion.a
              href={getWhatsAppLink(lead.phone_number) || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 items-center gap-2 cursor-pointer justify-center overflow-hidden rounded-lg bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white text-sm font-bold px-4 transition-colors flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/whatsapp-logo.png`}
                alt="WhatsApp"
                className="w-5 h-5 flex-shrink-0"
              />
              {t('Enviar mensagem')}
            </motion.a>
          )}
          <motion.button
            onClick={onClose}
            className="flex h-10 w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700/50 text-[#181411] dark:text-gray-300 text-sm font-bold px-4 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('dashboard.detail_close')}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LeadDetailModal;